/**
 * Cookie Manager for Cross-Domain Authentication
 * Handles both development (localhost) and production (subdomains) environments
 */

import type { AuthTokens, User, CookieOptions, AuthConfig } from "../types";

export class CookieManager {
  private static config: AuthConfig = {
    authDomain:
      process.env.NODE_ENV === "production"
        ? "https://app.theimmigrationgurus.ai"
        : "http://localhost:3000",
    cookieDomain:
      process.env.NODE_ENV === "production"
        ? ".theimmigrationgurus.ai"
        : "localhost",
    isDevelopment: process.env.NODE_ENV !== "production",
    roleRedirectMapping: {
      admin:
        process.env.NODE_ENV === "production"
          ? "https://app.admin.theimmigrationgurus.ai"
          : "http://localhost:3001",
      super_admin:
        process.env.NODE_ENV === "production"
          ? "https://app.admin.theimmigrationgurus.ai"
          : "http://localhost:3001",
      crm_user:
        process.env.NODE_ENV === "production"
          ? "https://app.crm.theimmigrationgurus.ai"
          : "http://localhost:3002",
      agent:
        process.env.NODE_ENV === "production"
          ? "https://app.crm.theimmigrationgurus.ai"
          : "http://localhost:3002",
      student:
        process.env.NODE_ENV === "production"
          ? "https://app.students.theimmigrationgurus.ai"
          : "http://localhost:3003",
      eglope_user:
        process.env.NODE_ENV === "production"
          ? "https://app.eglope.theimmigrationgurus.ai"
          : "http://localhost:3004",
    },
  };

  /**
   * Configure the cookie manager for your environment
   */
  static configure(config: Partial<AuthConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set authentication tokens in cookies
   * Handles both development and production environments
   */
  static setAuthTokens(tokens: AuthTokens, user?: User): void {
    const { accessToken, refreshToken } = tokens;

    // Access token - shorter expiry, readable by JS for API calls
    this.setSecureCookie("tig_access_token", accessToken, {
      maxAge: 15 * 60, // 15 minutes
      httpOnly: false, // JS needs to read it
      secure: !this.config.isDevelopment, // Only HTTPS in production
      sameSite: this.config.isDevelopment ? "lax" : "none", // "none" for cross-subdomain
      domain: this.config.isDevelopment ? undefined : this.config.cookieDomain,
    });

    // Refresh token - longer expiry, more secure
    if (refreshToken) {
      this.setSecureCookie("tig_refresh_token", refreshToken, {
        maxAge: 7 * 24 * 60 * 60, // 7 days
        httpOnly: false, // Allow JS access for token refresh
        secure: !this.config.isDevelopment,
        sameSite: this.config.isDevelopment ? "lax" : "none",
        domain: this.config.isDevelopment
          ? undefined
          : this.config.cookieDomain,
      });
    }

    // User info cookie - for quick access without API call
    if (user) {
      const userInfo = {
        id: user.id,
        email: user.email,
        role: user.role,
        name:
          user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      };

      this.setSecureCookie("tig_user_info", JSON.stringify(userInfo), {
        maxAge: 15 * 60, // Same as access token
        httpOnly: false,
        secure: !this.config.isDevelopment,
        sameSite: this.config.isDevelopment ? "lax" : "none",
        domain: this.config.isDevelopment
          ? undefined
          : this.config.cookieDomain,
      });
    }

    // Set auth flag for quick checks
    this.setSecureCookie("tig_authenticated", "true", {
      maxAge: 15 * 60,
      httpOnly: false,
      secure: !this.config.isDevelopment,
      sameSite: this.config.isDevelopment ? "lax" : "none",
      domain: this.config.isDevelopment ? undefined : this.config.cookieDomain,
    });

    console.log("🍪 Auth tokens set successfully", {
      isDevelopment: this.config.isDevelopment,
      domain: this.config.isDevelopment
        ? "localhost"
        : this.config.cookieDomain,
      user: user?.email,
    });
  }

  /**
   * Get access token from cookie
   */
  static getAccessToken(): string | null {
    return this.getCookie("tig_access_token");
  }

  /**
   * Get refresh token from cookie (usually for backend use)
   */
  static getRefreshToken(): string | null {
    return this.getCookie("tig_refresh_token");
  }

  /**
   * Get user info from cookie
   */
  static getUserInfo(): User | null {
    const userInfoStr = this.getCookie("tig_user_info");
    if (!userInfoStr) return null;

    try {
      return JSON.parse(userInfoStr);
    } catch {
      console.warn("Failed to parse user info from cookie");
      return null;
    }
  }

  /**
   * Check if user is authenticated (quick check)
   */
  static isAuthenticated(): boolean {
    const authFlag = this.getCookie("tig_authenticated");
    const token = this.getAccessToken();

    if (!authFlag || !token) return false;

    // Check if token is expired
    try {
      const payload = this.parseJWT(token);
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  /**
   * Clear all authentication cookies
   */
  static clearAuthTokens(): void {
    const cookieNames = [
      "tig_access_token",
      "tig_refresh_token",
      "tig_user_info",
      "tig_authenticated",
    ];

    cookieNames.forEach((name) => {
      this.deleteCookie(name);
    });

    this.clearSessionId();

    console.log("🗑️ Auth tokens cleared");
  }

  /**
   * Get redirect URL based on user role
   */
  static getRedirectUrlForRole(role: string): string {
    return (
      this.config.roleRedirectMapping[role] ||
      this.config.roleRedirectMapping.admin
    );
  }

  /**
   * Get auth service URL
   */
  static getAuthUrl(): string {
    return this.config.authDomain;
  }

  /**
   * A server-issued opaque session ID. It is not an authentication credential;
   * the API always verifies that it belongs to the authenticated user.
   */
  static setSessionId(sessionId: string): void {
    this.setSecureCookie("tig_session_id", sessionId, {
      maxAge: 7 * 24 * 60 * 60,
      httpOnly: false,
      secure: !this.config.isDevelopment,
      sameSite: this.config.isDevelopment ? "lax" : "none",
      domain: this.config.isDevelopment ? undefined : this.config.cookieDomain,
    });
  }

  static getSessionId(): string | null {
    return this.getCookie("tig_session_id");
  }

  static clearSessionId(): void {
    this.deleteCookie("tig_session_id");
  }

  /**
   * Development helper: Check if cookies are working
   */
  static testCookies(): boolean {
    const testValue = "test_" + Date.now();
    this.setSecureCookie("tig_test_cookie", testValue, { maxAge: 60 });
    const retrieved = this.getCookie("tig_test_cookie");
    this.deleteCookie("tig_test_cookie");

    const working = retrieved === testValue;
    console.log("🍪 Cookie test:", working ? "PASS" : "FAIL");
    return working;
  }

  /**
   * Private helper to set secure cookie
   */
  private static setSecureCookie(
    name: string,
    value: string,
    options: CookieOptions
  ): void {
    const optionParts: string[] = [
      `${name}=${encodeURIComponent(value)}`,
      "path=/",
    ];

    if (options.maxAge) optionParts.push(`max-age=${options.maxAge}`);
    if (options.httpOnly) optionParts.push("httponly");
    if (options.secure) optionParts.push("secure");
    if (options.sameSite) optionParts.push(`samesite=${options.sameSite}`);
    if (options.domain) optionParts.push(`domain=${options.domain}`);

    const cookieString = optionParts.join("; ");
    document.cookie = cookieString;

    if (this.config.isDevelopment) {
      console.log("🍪 Setting cookie:", cookieString);
    }
  }

  /**
   * Private helper to get cookie value
   */
  private static getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;

    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((row) => row.startsWith(`${name}=`));

    if (!cookie) return null;

    const value = cookie.split("=").slice(1).join("="); // Handle values with = in them
    const decodedValue = value ? decodeURIComponent(value) : null;

    if (this.config.isDevelopment && decodedValue) {
      console.log(
        "🍪 Getting cookie:",
        name,
        decodedValue ? "found" : "not found"
      );
    }

    return decodedValue;
  }

  /**
   * Private helper to delete cookie
   */
  private static deleteCookie(name: string): void {
    const deleteOptions: string[] = [
      `${name}=`,
      "path=/",
      "expires=Thu, 01 Jan 1970 00:00:01 GMT",
    ];

    // Delete for both current domain and parent domain
    if (this.config.isDevelopment) {
      document.cookie = deleteOptions.join("; ");
    } else {
      // Delete for specific domain (with dot prefix for subdomains)
      document.cookie = [
        ...deleteOptions,
        `domain=${this.config.cookieDomain}`,
      ].join("; ");
      // Also try without domain (fallback)
      document.cookie = deleteOptions.join("; ");
    }

    console.log(`🗑️ Deleting cookie: ${name}`);
  }

  /**
   * Private helper to parse JWT token
   */
  private static parseJWT(token: string): any {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error("Invalid JWT token");
    }
  }
}
